import { imageUrl, } from '@/utils/utils';

export const DEFAULT_AVATAR = '/gravewalker-urso.png';

/** `/placeholder-book.jpg` — último recurso em `src`; se também falhar, use `ObraCoverImage` para o estado com ícone. */
export const PLACEHOLDER_OBRA_COVER = '/placeholder-book.jpg';

/** Alias alinhado ao app (`ObraCard`, listas horizontais). */
export const FALLBACK_IMAGE_URL = PLACEHOLDER_OBRA_COVER;

export function applyFallbackImageOnError(img: HTMLImageElement): void {
    if (img.src.endsWith(FALLBACK_IMAGE_URL) || img.src.endsWith(PLACEHOLDER_OBRA_COVER)) return;
    img.src = FALLBACK_IMAGE_URL;
}

/**
 * URL da capa da obra no CDN — ficheiro original, sem proxy de redimensionamento (`/media/obras/.../capa`).
 * Os parâmetros `w` e `q` mantêm-se na assinatura por compatibilidade com chamadas existentes; são ignorados.
 */
export const getObraImageUrl = (
    obraId: number,
    imagem: string | null | undefined,
    _w?: number,
    _q = 70,
): string | null => {
    if (!imagem || !obraId) return null;

    if (imagem.startsWith('http://') || imagem.startsWith('https://')) {
        return imagem;
    }

    const cleanImage = imagem.replace(/^\/+/, '');
    return `${imageUrl}obras/${obraId}/${cleanImage}`;
};

/**
 * Constrói a URL completa do banner de uma obra
 * @param obraId - ID da obra
 * @param banner - Nome do arquivo do banner
 * @returns URL completa do banner ou null se não houver banner
 */
export const getObraBannerUrl = (obraId: number, banner: string | null | undefined): string | null => {
    if (!banner || !obraId) return null;

    // Se já é uma URL completa, retorna direto
    if (banner.startsWith('http://') || banner.startsWith('https://')) {
        return banner;
    }

    // Remove barras iniciais se houver
    const cleanBanner = banner.replace(/^\/+/, '');

    // Constrói a URL: cdn.mediocretoons.site/obras/{obraId}/{banner}
    return `${imageUrl}obras/${obraId}/${cleanBanner}`;
};

/**
 * Constrói a URL completa da imagem de um capítulo
 * @deprecated Esta função não é mais usada. O sistema atual usa tokens via /cdn/t/{token}
 * @param obraId - ID da obra
 * @param capituloNum - Número do capítulo
 * @param imagem - Nome do arquivo da imagem
 * @param userId - ID do usuário para proteção (obrigatório para capítulos)
 * @returns URL completa da imagem protegida ou null se não houver imagem/userId
 */
export const getCapituloImageUrl = (
    obraId: number,
    capituloNum: number,
    imagem: string | null | undefined,
    userId?: number | string | null
): string | null => {
    // Sistema antigo removido - agora usa /cdn/t/{token}
    // Mantida apenas para compatibilidade com código legado
    console.warn('[DEPRECATED] getCapituloImageUrl está deprecated. Use o sistema de tokens /cdn/t/{token}');
    return null;
};

/**
 * Constrói a URL completa da imagem de um usuário
 * @param userId - ID do usuário
 * @param imagem - Nome do arquivo da imagem
 * @returns URL completa da imagem ou null se não houver imagem
 */
export const getUserImageUrl = (
    userId: number | string,
    imagem: string | null | undefined
): string => {
    if (!imagem || !userId) return DEFAULT_AVATAR;

    // Se já é uma URL completa, retorna direto
    if (imagem.startsWith('http://') || imagem.startsWith('https://') || imagem.startsWith('data:')) {
        return imagem;
    }

    // Remove barras iniciais se houver
    const cleanImage = imagem.replace(/^\/+/, '');

    // Constrói a URL: cdn.mediocretoons.site/usuarios/{userId}/{cleanImage}
    return `${imageUrl}usuarios/${userId}/${cleanImage}`;
};

/**
 * Constrói a URL completa do banner de um usuário
 * @param userId - ID do usuário
 * @param banner - Nome do arquivo do banner
 * @returns URL completa do banner ou null se não houver banner
 */
export const getUserBannerUrl = (
    userId: number | string,
    banner: string | null | undefined
): string | null => {
    if (!banner || !userId) return null;

    // Se já é uma URL completa, retorna direto
    if (banner.startsWith('http://') || banner.startsWith('https://') || banner.startsWith('data:')) {
        return banner;
    }

    // Remove barras iniciais se houver
    const cleanBanner = banner.replace(/^\/+/, '');

    // Constrói a URL: cdn.mediocretoons.site/usuarios/{userId}/{cleanBanner}
    return `${imageUrl}usuarios/${userId}/${cleanBanner}`;
};


export const getMolduraImageUrl = (imagem: string | null | undefined): string | null => {
    if (!imagem) return null;

    // Se já é uma URL completa, retorna direto
    if (imagem.startsWith('http://') || imagem.startsWith('https://') || imagem.startsWith('data:')) {
        return imagem;
    }

    // Remove barras iniciais se houver
    const cleanImage = imagem.replace(/^\/+/, '');

    // Constrói a URL: cdn.mediocretoons.site/molduras/{cleanImage}
    return `${imageUrl}molduras/${cleanImage}?v=9989`;
};

/**
 * Forma do recorte do avatar conforme `mol_tipo` no banco/API:
 * - `1` = moldura redonda → avatar circular (`rounded-full`)
 * - `2` = moldura quadrada → avatar com cantos (`rounded-lg`)
 */
export function avatarClipClassFromMolduraTipo(molTipo: number | null | undefined): 'rounded-full' | 'rounded-lg' {
    return molTipo === 2 ? 'rounded-lg' : 'rounded-full';
}

/** Avatar no cabeçalho do perfil: tipo 2 força quadrado; tipo 1 circular; senão estilo perfil2. */
export function perfilAvatarShapeClass(
    molTipo: number | null | undefined,
    perfilStyle: 'default' | 'perfil2'
): string {
    if (molTipo === 2) return 'rounded-lg';
    if (perfilStyle === 'perfil2') return 'rounded-xl';
    return 'rounded-full';
}

/** Preview no modal de edição (perfil2 usa cantos maiores no layout do modal). */
export function editModalAvatarShapeClass(
    molTipo: number | null | undefined,
    perfilStyle: 'default' | 'perfil2'
): string {
    if (molTipo === 2) return 'rounded-lg';
    if (perfilStyle === 'perfil2') return 'rounded-3xl';
    return 'rounded-full';
}

/**
 * Constrói a URL completa da imagem de uma tag de usuário
 * @param imagem - Nome do arquivo da imagem
 * @returns URL completa da imagem ou null se não houver imagem
 */
export const getTagImageUrl = (imagem: string | null | undefined): string | null => {
    if (!imagem) return null;

    // Se já é uma URL completa, retorna direto
    if (imagem.startsWith('http://') || imagem.startsWith('https://')) {
        return imagem;
    }

    // Remove barras iniciais se houver
    const cleanImage = imagem.replace(/^\/+/, '');

    // Constrói a URL: cdn.mediocretoons.site/usuario-tags/{imagem}
    return `${imageUrl}usuario-tags/${cleanImage}`;
};
